import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui';

const CONFIRM_PHRASE = 'XOA';

interface Props {
    open: boolean;
    count: number;
    pending: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}

/**
 * EAA-1820 (frame 34): destructive confirm for ≥2 selected accounts — names
 * the blast radius and requires typing "XOA" before the destructive button
 * enables. A single-row delete keeps using the existing `ConfirmButton`
 * (native Popconfirm) already wired in `AccountsPage.tsx`; this typed
 * variant is reserved for the multi-row case the ticket calls out
 * specifically ("≥ 2 items require typing XOA").
 */
export function BulkDeleteConfirmModal({ open, count, pending, onCancel, onConfirm }: Props) {
    const { t } = useTranslation();
    const [typed, setTyped] = useState('');

    const close = () => {
        setTyped('');
        onCancel();
    };

    const confirmed = typed.trim().toUpperCase() === CONFIRM_PHRASE;

    return (
        <Modal
            open={open}
            onCancel={close}
            title={t('console.accounts.bulkDeleteModal.title', { count })}
            footer={
                <>
                    <button type="button" className="portal-btn" onClick={close} disabled={pending}>
                        {t('common.cancel')}
                    </button>
                    <button
                        type="button"
                        className="portal-btn portal-btn-danger"
                        disabled={!confirmed || pending}
                        onClick={onConfirm}
                    >
                        {pending
                            ? t('console.accounts.bulkDeleteModal.pending')
                            : t('console.accounts.bulkDeleteModal.submit', { count })}
                    </button>
                </>
            }
        >
            <div className="portal-admin-form" style={{ gridTemplateColumns: '1fr' }}>
                <p className="portal-admin-hint">
                    {t('console.accounts.bulkDeleteModal.hint', { count })}
                </p>

                {/* EAA-1820 (frame 68): the two consequences the frame requires
                    be stated. Neither is cosmetic — the audit NOT cascading is
                    what makes the delete traceable afterwards, and the
                    assignments NOT cascading is a real cleanup the operator owns
                    and would otherwise discover later as orphaned rows. */}
                <ul className="acc-delete-consequences">
                    <li>
                        <strong>{t('console.accounts.bulkDeleteModal.auditLogTerm')}</strong>{' '}
                        {t('console.accounts.bulkDeleteModal.auditLogNote')}
                    </li>
                    <li>
                        <strong>{t('console.accounts.bulkDeleteModal.assignmentsTerm')}</strong>{' '}
                        {t('console.accounts.bulkDeleteModal.assignmentsNote')}
                    </li>
                </ul>
                <label>
                    {/* EAA-1818: wrapped for the same reason as the create-đơn-vị
                        modal — the label is a column flex container, so the inline
                        <code> split this caption across three lines. */}
                    <span className="portal-field-label">
                        {t('console.accounts.bulkDeleteModal.confirmLabelPrefix')} <code>XOA</code>{' '}
                        {t('console.accounts.bulkDeleteModal.confirmLabelSuffix')}
                    </span>
                    <input
                        value={typed}
                        onChange={(e) => setTyped(e.target.value)}
                        placeholder="XOA"
                        autoFocus
                    />
                </label>
            </div>
        </Modal>
    );
}
